import {
  SalientPageLayout,
  Container,
  Button,
  TextField,
  SelectField,
  TextAreaField,
  Card,
  CardHeader,
  CardContent,
  CardFooter,
  Badge,
  CallToAction,
  SlimLayout,
  Hero,
} from './index'

export function EnhancedUIDemo() {
  return (
    <SalientPageLayout>
      <Container className="py-16">
        <div className="text-center mb-16">
          <h1 className="font-display text-4xl font-medium tracking-tight text-slate-900 sm:text-6xl">
            Enhanced Salient UI Components
          </h1>
          <p className="mt-6 text-lg tracking-tight text-slate-700">
            A comprehensive showcase of all Salient UI components including forms, cards, badges, and more.
          </p>
        </div>

        {/* Form Fields Section */}
        <section className="mb-16">
          <h2 className="font-display text-2xl font-semibold text-slate-900 mb-8">Form Fields</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card>
              <CardHeader>
                <h3 className="font-display text-xl font-semibold text-slate-900">Text Fields</h3>
              </CardHeader>
              <CardContent className="space-y-4">
                <TextField label="Email Address" type="email" placeholder="you@company.com" />
                <TextField label="Password" type="password" placeholder="••••••••" />
                <TextField label="Company Name" placeholder="Your Company" />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <h3 className="font-display text-xl font-semibold text-slate-900">Select & Textarea</h3>
              </CardHeader>
              <CardContent className="space-y-4">
                <SelectField label="Country">
                  <option value="">Select a country</option>
                  <option value="us">United States</option>
                  <option value="ca">Canada</option>
                  <option value="uk">United Kingdom</option>
                </SelectField>
                <TextAreaField label="Description" placeholder="Tell us about your project..." rows={4} />
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Cards Section */}
        <section className="mb-16">
          <h2 className="font-display text-2xl font-semibold text-slate-900 mb-8">Card Components</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card>
              <CardHeader>
                <h3 className="font-display text-lg font-semibold text-slate-900">Default Card</h3>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600">This is a default card with standard styling.</p>
              </CardContent>
              <CardFooter>
                <Button variant="solid" color="blue">Action</Button>
              </CardFooter>
            </Card>

            <Card variant="elevated">
              <CardHeader>
                <h3 className="font-display text-lg font-semibold text-slate-900">Elevated Card</h3>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600">This card has elevated styling with shadow.</p>
              </CardContent>
              <CardFooter>
                <Button variant="outline" color="slate">Secondary</Button>
              </CardFooter>
            </Card>

            <Card variant="outlined">
              <CardHeader>
                <h3 className="font-display text-lg font-semibold text-slate-900">Outlined Card</h3>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600">This card has a prominent border outline.</p>
              </CardContent>
              <CardFooter>
                <Button variant="solid" color="blue">Primary</Button>
              </CardFooter>
            </Card>
          </div>
        </section>

        {/* Badges Section */}
        <section className="mb-16">
          <h2 className="font-display text-2xl font-semibold text-slate-900 mb-8">Badge Components</h2>
          <div className="space-y-6">
            <div>
              <h3 className="font-display text-lg font-semibold text-slate-900 mb-4">Variants</h3>
              <div className="flex flex-wrap gap-3">
                <Badge>Default</Badge>
                <Badge variant="success">Success</Badge>
                <Badge variant="warning">Warning</Badge>
                <Badge variant="error">Error</Badge>
                <Badge variant="info">Info</Badge>
              </div>
            </div>

            <div>
              <h3 className="font-display text-lg font-semibold text-slate-900 mb-4">Sizes</h3>
              <div className="flex flex-wrap items-center gap-3">
                <Badge size="sm">Small</Badge>
                <Badge size="md">Medium</Badge>
                <Badge size="lg">Large</Badge>
              </div>
            </div>
          </div>
        </section>

        {/* Buttons Section */}
        <section className="mb-16">
          <h2 className="font-display text-2xl font-semibold text-slate-900 mb-8">Button Components</h2>
          <div className="space-y-6">
            <div>
              <h3 className="font-display text-lg font-semibold text-slate-900 mb-4">Variants</h3>
              <div className="flex flex-wrap gap-3">
                <Button variant="solid" color="blue">Primary</Button>
                <Button variant="outline" color="slate">Secondary</Button>
                <Button variant="solid" color="white">White</Button>
              </div>
            </div>

            <div>
              <h3 className="font-display text-lg font-semibold text-slate-900 mb-4">Sizes</h3>
              <div className="flex flex-wrap items-center gap-3">
                <Button variant="solid" color="blue">Small</Button>
                <Button variant="solid" color="blue">Medium</Button>
                <Button variant="solid" color="blue">Large</Button>
              </div>
            </div>
          </div>
        </section>

        {/* Hero Component Section */}
        <section className="mb-16">
          <h2 className="font-display text-2xl font-semibold text-slate-900 mb-8">Hero Component</h2>
          <Card>
            <CardContent className="p-0">
              <Hero
                title="Stop Dreading Certified Payroll"
                subtitle="Start Producing It in Minutes. Finally, a simple, guided tool that transforms complex certified payroll into a quick, error-free process."
                primaryAction={{
                  text: "Get Early Access",
                  href: "/register"
                }}
                secondaryAction={{
                  text: "Learn More",
                  href: "/about"
                }}
                showLogos={true}
                logos={[
                  {
                    name: "Company 1",
                    src: "/logo1.png",
                    alt: "Company 1 Logo",
                  },
                  {
                    name: "Company 2",
                    src: "/logo2.png",
                    alt: "Company 2 Logo",
                  },
                  {
                    name: "Company 3",
                    src: "/logo3.png",
                    alt: "Company 3 Logo",
                  }
                ]}
              />
            </CardContent>
          </Card>
        </section>

        {/* SlimLayout Component Section */}
        <section className="mb-16">
          <h2 className="font-display text-2xl font-semibold text-slate-900 mb-8">SlimLayout Component</h2>
          <Card>
            <CardContent className="p-0">
              <div className="border rounded-lg overflow-hidden">
                <SlimLayout showFooter={true}>
                  <Card className="border-0 shadow-none">
                    <CardHeader>
                      <h3 className="font-display text-xl font-semibold text-slate-900">Login Form</h3>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <TextField label="Email Address" type="email" placeholder="you@company.com" />
                      <TextField label="Password" type="password" placeholder="••••••••" />
                      <Button variant="solid" color="blue" className="w-full">
                        Sign In
                      </Button>
                    </CardContent>
                  </Card>
                </SlimLayout>
              </div>
            </CardContent>
          </Card>
        </section>
      </Container>

      {/* Call to Action Section */}
      <CallToAction
        title="Ready to get started?"
        description="Join thousands of users who are already using our platform to manage their projects efficiently."
        primaryAction={{
          text: "Get Started Today",
          to: "/dashboard"
        }}
        secondaryAction={{
          text: "Learn More",
          to: "/company-info"
        }}
        className="bg-blue-600 py-32"
      />
    </SalientPageLayout>
  )
} 